import { useEffect, useState } from "react";
import { DeliveryService, type LaunchDeliveryConfig } from "@services/delivery/deliveryService";
import { SequenceService, type SenderIdentity } from "@services/sequence/sequenceService";

/** Nothing set yet, which is a state the previews have to render. */
const NO_SENDER: SenderIdentity = {
  from_name: "", from_address: "", sms_sender: "", footer_address: "",
};

/*
    Whether this campaign may send on each channel at all (MPN-2203).

    Set on the campaign's own Email and Text messages cards, not here: a playbook can only
    offer a channel the campaign has switched on, and a touch in a dead channel is not a
    thing that will send. These were hardcoded `true` until the cards existed.
*/
export function useCampaignChannels(launchId: number | null) {
  const [channels, setChannels] = useState({ email: false, sms: false });
  //: The email channel's own row, which names the account this campaign sends through.
  const [emailChannel, setEmailChannel] = useState<LaunchDeliveryConfig | null>(null);
  //: The SMS row, which names the number texts go out from.
  const [smsChannel, setSmsChannel] = useState<LaunchDeliveryConfig | null>(null);
  /*
      Who this campaign's messages come from, for every preview on the screen (MPN-2201).

      Resolved server-side rather than assembled here: the From address is on the campaign's
      email row, the sending number is the platform's, and the footer address is the one
      `email_body` appends at send time -- three places the browser cannot reach. These
      previews used to carry one live customer's sender name, email domain, phone number and
      street address as constants, the client approval preview included, where a different
      client would have been shown another school's details as their own.
  */
  const [sender, setSender] = useState<SenderIdentity>(NO_SENDER);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!launchId) {
      setChannels({ email: false, sms: false });
      setSender(NO_SENDER);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    new DeliveryService()
      .listLaunchChannels(launchId)
      .then((configs) => {
        if (cancelled) return;
        const on = (channel: string) =>
          !!configs.find((c) => c.channel === channel)?.is_enabled;
        setChannels({ email: on("email"), sms: on("sms") });
        setEmailChannel(configs.find((c) => c.channel === "email") ?? null);
        setSmsChannel(configs.find((c) => c.channel === "sms") ?? null);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    new SequenceService()
      .getSenderIdentity(launchId)
      .then((identity) => {
        if (!cancelled) setSender(identity);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [launchId]);

  return { channels, emailChannel, smsChannel, sender, isLoading };
}

